import { useState } from "react";
import { Key, Trash2, RefreshCw, Copy, Check, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import type { ApiToken, CreateTokenResponse } from "@/lib/api/tokens";

// =============================================================================
// Types
// =============================================================================

interface ApiKeyListProps {
  tokens: ApiToken[];
  onRevoke: (id: string) => void;
  isRevoking?: boolean;
  /**
   * Rotate a key. Resolves with the rotated token including its new plaintext
   * secret, which is then revealed once. Optional — rotation UI is hidden when
   * not provided.
   */
  onRotate?: (id: string) => Promise<CreateTokenResponse>;
  isRotating?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function ApiKeyList({
  tokens,
  onRevoke,
  isRevoking,
  onRotate,
  isRotating,
}: ApiKeyListProps) {
  const [tokenToRevoke, setTokenToRevoke] = useState<ApiToken | null>(null);
  const [tokenToRotate, setTokenToRotate] = useState<ApiToken | null>(null);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleConfirmRevoke = () => {
    if (tokenToRevoke) {
      onRevoke(tokenToRevoke.id);
      setTokenToRevoke(null);
    }
  };

  const handleConfirmRotate = async () => {
    if (!tokenToRotate || !onRotate) return;
    try {
      const result = await onRotate(tokenToRotate.id);
      setTokenToRotate(null);
      setRotatedSecret(result.token);
    } catch {
      // Error handling is managed by the mutation; keep the dialog open.
    }
  };

  const handleCopy = async () => {
    if (!rotatedSecret) return;
    try {
      await navigator.clipboard.writeText(rotatedSecret);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    } catch {
      // Ignore copy failures; the value is still visible for manual copy.
    }
  };

  const handleCloseReveal = () => {
    setRotatedSecret(null);
    setCopied(false);
  };

  if (tokens.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
        <Key className="h-8 w-8 mb-2 opacity-50" />
        <p>No API keys</p>
        <p className="text-sm">Create an API key to get started</p>
      </div>
    );
  }

  return (
    <>
      <div className="divide-y">
        {tokens.map((token) => (
          <div
            key={token.id}
            className="flex items-start justify-between py-4 first:pt-0 last:pb-0"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-muted p-2">
                <Key className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{token.name}</span>
                  {token.scopes.length > 0 && (
                    <span className="text-sm text-muted-foreground">
                      · {token.scopes.join(", ")}
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Created {formatDistanceToNow(new Date(token.createdAt), { addSuffix: true })}
                  {" · "}
                  {token.lastUsedAt
                    ? `Last used ${formatDistanceToNow(new Date(token.lastUsedAt), { addSuffix: true })}`
                    : "never used"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {onRotate && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setTokenToRotate(token); }}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Rotate
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setTokenToRevoke(token); }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Revoke
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Revoke confirmation dialog */}
      <Dialog open={!!tokenToRevoke} onOpenChange={() => { setTokenToRevoke(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke this API key? Any applications using
              this key will no longer be able to access the API.
            </DialogDescription>
          </DialogHeader>

          {tokenToRevoke && (
            <div className="rounded-md border p-4 bg-muted/50">
              <p className="font-medium">{tokenToRevoke.name}</p>
              <p className="text-sm text-muted-foreground">
                Created {formatDistanceToNow(new Date(tokenToRevoke.createdAt), { addSuffix: true })}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setTokenToRevoke(null); }}
              disabled={isRevoking}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRevoke}
              disabled={isRevoking}
            >
              {isRevoking ? "Revoking..." : "Revoke Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate confirmation dialog */}
      <Dialog open={!!tokenToRotate} onOpenChange={() => { setTokenToRotate(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rotate API Key</DialogTitle>
            <DialogDescription>
              Rotating issues a new secret for this key. The current secret stops
              working immediately, so any client using it must be updated with
              the new value.
            </DialogDescription>
          </DialogHeader>

          {tokenToRotate && (
            <div className="rounded-md border p-4 bg-muted/50">
              <p className="font-medium">{tokenToRotate.name}</p>
              <p className="text-sm text-muted-foreground">
                Created {formatDistanceToNow(new Date(tokenToRotate.createdAt), { addSuffix: true })}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setTokenToRotate(null); }}
              disabled={isRotating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => { void handleConfirmRotate(); }}
              disabled={isRotating}
            >
              {isRotating ? "Rotating..." : "Rotate Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New-secret reveal dialog (shown once after rotation) */}
      <Dialog
        open={!!rotatedSecret}
        onOpenChange={(open) => { if (!open) handleCloseReveal(); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Key Rotated</DialogTitle>
            <DialogDescription>
              Your API key has a new secret. Update your clients with the value below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <p className="text-sm">
                Copy this key now. You won't be able to see it again!
              </p>
            </div>

            <div className="space-y-2">
              <Label>Your New API Key</Label>
              <div className="flex gap-2">
                <Input
                  value={rotatedSecret ?? ""}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => { void handleCopy(); }}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleCloseReveal}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
